import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { ProcureAction, ProcureGetter } from '@/store/procure/procure.module';
import { ProcureAttachmentSchema } from '@/store/procure/procure.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ProjectAttachmentFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    projectId: {
      type: Number,
    },
  },
  emits: [
    'hidden'
  ],
  setup: (props, context) => {
    const store = useStore();
    const attachmentForm = ref<InstanceType<typeof BaseModalComponent>>();
    const fileDialog = ref<HTMLInputElement>();

    // form
    interface Form {
      projectId?: number | null;
      attachmentTypeId: number;
      name?: string | null;
      file?: string | null;
    }
    const formData = reactive<Form>({
      projectId: props.projectId,
      attachmentTypeId: 1,
      name: '',
      file: ''
    });
    const validator = computed(() => ProcureAttachmentSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      file: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'file'))
    });
    const isValid = computed(() => !errors.name && !errors.file);
    const isLoading = computed<boolean>(() => store.getters[ProcureGetter.STATUS] === 'init' || store.getters[ProcureGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      Promise.resolve(formData.projectId = props.projectId)
        .then(() => attachmentForm.value?.show());
    };

    // hide modal
    const hide = () => {
      attachmentForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.attachmentTypeId = 1;
        formData.name = '';
        formData.file = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        const files = fileDialog.value?.files as FileList;
        store.dispatch(ProcureAction.ADD_ATTACHMENTS, {
          projectId: formData.projectId,
          attachmentTypeId: formData.attachmentTypeId,
          names: [formData.name],
          attachments: files,
        }).then(() => hide());
      }
    };

    const fileDialogChange = () => {
      const files = fileDialog.value?.files as FileList;
      const file = files.item(0) as File;
      formData.file = file.name;
      unsubmit();
    };

    const handleHidden = () => {
      context.emit('hidden', props.projectId);
    };

    return {
      formData,
      attachmentForm,
      fileDialog,
      isLoading,
      errors,
      submited,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
      fileDialogChange,
      handleHidden,
    };
  },
});
